import { intl } from '@fullstory/intl';
import { timestamp } from '@fullstory/proto/google/protobuf';
import { LibraryObjectType } from '@fullstory/routes';
import { assertExhaustive } from '@fullstory/typeutils';
import React from 'react';
import { RemoveModal } from '../RemoveModal';

// specifically calling out that dashboards are the only object type that can still be deleted
type Mode = 'remove' | 'delete-dashboard' | 'archive';

const PRIMARY_ACTION_BUTTON_TEXT: { [mode in Mode]: string } = {
  remove: intl.formatMessage({ defaultMessage: 'Remove', id: 'G/yZLu' }),
  'delete-dashboard': intl.formatMessage({ defaultMessage: 'Delete', id: 'K3r6DQ' }),
  archive: intl.formatMessage({ defaultMessage: 'Archive', id: 'hrgo+E' }),
};

type RemoveModalProps = Pick<
  React.ComponentProps<typeof RemoveModal>,
  'open' | 'onRemove' | 'onCloseSignal'
>;

interface RemoveObjectModalProps extends RemoveModalProps {
  /**
   * The removal mode - remove (a linked/referenced object), archive, or delete.
   */
  mode: Mode;
  /**
   * Type of object.
   */
  objectType: LibraryObjectType;
  /**
   * Last updated date of the object.
   */
  objectLastUpdated: timestamp | null;
  /**
   * Last updated by user (this will be the email).
   */
  objectLastUpdatedBy: string;
  /**
   * Messaging is different when mode = removed.
   *
   * @default false
   */
  isFeaturedInCurrentSpaceContext?: boolean;
}

/**
 * Specific use of RemoveModal geared around object removal actions (see modes).
 */
export const RemoveObjectModal: React.FC<RemoveObjectModalProps> = ({
  open,
  onRemove,
  onCloseSignal,
  mode,
  objectType,
  objectLastUpdated,
  objectLastUpdatedBy,
  isFeaturedInCurrentSpaceContext = false,
}) => {
  let message = '';
  let hint = '';

  switch (mode) {
    case 'archive': {
      message = intl.formatMessage(
        {
          defaultMessage:
            'This {objectType, select, 1 {metric} 2 {funnel} 3 {retention} 5 {segment} 6 {dashboard} 7 {heatmap} 8 {journey} other {item}} will be archived.',
          id: 'eNdx9J',
        },
        { objectType },
      );
      hint = intl.formatMessage({
        defaultMessage: 'You can recover an archived item by using the filter options in Library.',
        id: 'Xnf0xv',
      });
      break;
    }
    case 'delete-dashboard': {
      message = intl.formatMessage({
        defaultMessage: 'This dashboard will be permanently deleted.',
        id: 'bL1/JF',
      });
      hint = intl.formatMessage(
        {
          defaultMessage:
            '{lastUpdatedBy} most recently updated this dashboard on {lastUpdated, date, ::Mdyy} at {lastUpdated, time, short}.',
          id: 'GjiaEq',
        },
        {
          lastUpdated: new Date(objectLastUpdated as string),
          lastUpdatedBy: objectLastUpdatedBy,
        },
      );
      break;
    }
    case 'remove': {
      if (isFeaturedInCurrentSpaceContext) {
        message = intl.formatMessage(
          {
            defaultMessage:
              'This {objectType, select, 1 {metric} 2 {funnel} 3 {retention} 5 {segment} 6 {dashboard} 7 {heatmap} 8 {journey} other {item}} is currently Featured.',
            id: 'QegMpw',
          },
          {
            objectType,
          },
        );
        hint = intl.formatMessage(
          {
            defaultMessage:
              'Removing this {objectType, select, 1 {metric} 2 {funnel} 3 {retention} 5 {segment} 6 {dashboard} 7 {heatmap} 8 {journey} other {item}} from the Library will also remove it from Featured. You can add it back to this Library at any time.',
            id: 'eRaufK',
          },
          { objectType },
        );
      } else {
        message = intl.formatMessage(
          {
            defaultMessage:
              "This {objectType, select, 1 {metric} 2 {funnel} 3 {retention} 5 {segment} 6 {dashboard} 7 {heatmap} 8 {journey} other {item}} will no longer be visible in this space's Library tab.",
            id: 'kbszQN',
          },
          { objectType },
        );
        hint = intl.formatMessage({
          defaultMessage: 'You can add it back to this Library at any time.',
          id: 'xkep+5',
        });
      }
      break;
    }
    default:
      assertExhaustive(mode);
  }

  return (
    <RemoveModal
      title={intl.formatMessage({ defaultMessage: 'Are you sure?', id: '2oCaym' })}
      open={open}
      onRemove={onRemove}
      onCloseSignal={onCloseSignal}
      message={message}
      hint={hint}
      primaryActionText={PRIMARY_ACTION_BUTTON_TEXT[mode]}
    />
  );
};
